<template>
  <div class="recipe-card" :class="{ expanded: isExpanded, favorite: recipe.is_favorite }" @click="toggleExpand">
    <div class="recipe-header">
      <div class="recipe-main-info">
        <div class="recipe-badges">
          <span class="category-badge" :style="{ backgroundColor: getCategoryColor(recipe.category) }">
            {{ getCategoryLabel(recipe.category) }}
          </span>
          <span v-if="recipe.difficulty && recipe.difficulty !== 'easy'" class="difficulty-badge"
            :style="{ backgroundColor: getDifficultyColor(recipe.difficulty) }">
            {{ recipe.difficulty }}
          </span>
        </div>
        <h4 class="recipe-name">{{ recipe.name }}</h4>
        <p class="recipe-description">{{ recipe.description }}</p>
      </div>

      <div class="recipe-actions">
        <button class="favorite-btn" :class="{ active: recipe.is_favorite }" @click.stop="toggleFavorite"
          :disabled="favoriteLoading">
          <span v-if="recipe.is_favorite">&#9733;</span>
          <span v-else>&#9734;</span>
        </button>
      </div>
    </div>

    <div class="recipe-meta">
      <div class="meta-row">
        <div class="meta-item">
          <span class="meta-icon meal-type-icon">{{ getMealTypeSymbol(recipe.meal_type) }}</span>
          <span class="meta-value">{{ getMealTypeLabel(recipe.meal_type) }}</span>
        </div>
        <div v-if="recipe.prep_time_mins" class="meta-item">
          <span class="meta-icon time-icon">T</span>
          <span class="meta-value">{{ recipe.prep_time_mins }} min</span>
        </div>
        <div v-if="recipe.calories" class="meta-item">
          <span class="meta-icon cal-icon">K</span>
          <span class="meta-value">{{ recipe.calories }} kcal</span>
        </div>
      </div>

      <div class="macros-row">
        <div class="macro-badge carbs">
          <span class="macro-letter">C</span>
          <span class="macro-value">{{ recipe.carbs || 0 }}g</span>
        </div>
        <div class="macro-badge protein">
          <span class="macro-letter">P</span>
          <span class="macro-value">{{ recipe.protein || 0 }}g</span>
        </div>
        <div class="macro-badge fat">
          <span class="macro-letter">F</span>
          <span class="macro-value">{{ recipe.fat || 0 }}g</span>
        </div>
      </div>

      <div v-if="parsedDietaryTags.length" class="dietary-tags">
        <span v-for="tag in parsedDietaryTags" :key="tag" class="dietary-tag"
          :style="{ backgroundColor: getDietaryColor(tag) }">
          {{ formatDietaryTag(tag) }}
        </span>
      </div>
    </div>

    <div v-if="isExpanded" class="recipe-details">
      <div v-if="recipe.ingredients" class="detail-section">
        <h5>Ingredients</h5>
        <p>{{ recipe.ingredients }}</p>
      </div>

      <div v-if="recipe.timing_window" class="detail-section">
        <h5>Best Timing</h5>
        <p class="timing-info">{{ formatTimingWindow(recipe.timing_window) }}</p>
      </div>

      <button class="quick-add-btn" @click.stop="quickAdd">
        + Quick Add to Log
      </button>
    </div>

    <div class="expand-indicator">
      <span v-if="!isExpanded">&#9660;</span>
      <span v-else>&#9650;</span>
    </div>
  </div>
</template>

<script>
import Nutrition from "@/services/nutrition-service";

export default {
  name: "RecipeCard",

  props: {
    recipe: {
      type: Object,
      required: true,
    },
    userId: {
      type: [Number, String],
      default: null,
    },
  },

  data() {
    return {
      isExpanded: false,
      favoriteLoading: false,
    };
  },

  computed: {
    parsedDietaryTags() {
      if (!this.recipe.dietary_tags) return [];
      if (typeof this.recipe.dietary_tags === "string") {
        try {
          return JSON.parse(this.recipe.dietary_tags) || [];
        } catch {
          return [];
        }
      }
      return this.recipe.dietary_tags || [];
    },
  },

  methods: {
    toggleExpand() {
      this.isExpanded = !this.isExpanded;
    },

    async toggleFavorite() {
      if (!this.userId || this.favoriteLoading) return;

      this.favoriteLoading = true;
      try {
        const response = await Nutrition.toggleFavorite(
          this.userId,
          this.recipe.id
        );
        if (response.data && response.data.success) {
          this.$emit("favorite-toggled", {
            templateId: this.recipe.id,
            favorited: response.data.favorited,
          });
        }
      } catch (error) {
        console.error("Error toggling favorite:", error);
      } finally {
        this.favoriteLoading = false;
      }
    },

    quickAdd() {
      this.$emit("quick-add", this.recipe);
    },

    getMealTypeSymbol(mealType) {
      const info = Nutrition.getMealTypeInfo(mealType);
      return info.symbol || "M";
    },

    getMealTypeLabel(mealType) {
      const info = Nutrition.getMealTypeInfo(mealType);
      return info.label || mealType;
    },

    getCategoryLabel(category) {
      const categories = {
        quick: "Quick",
        main: "Main",
        light: "Light",
        hearty: "Hearty",
        liquid: "Shake",
      };
      return categories[category] || category || "Main";
    },

    getCategoryColor(category) {
      const colors = {
        quick: "#28a745",
        main: "#007bff",
        light: "#17a2b8",
        hearty: "#fd7e14",
        liquid: "#6f42c1",
      };
      return colors[category] || "#6c757d";
    },

    getDifficultyColor(difficulty) {
      const colors = {
        easy: "#28a745",
        medium: "#ffc107",
        advanced: "#dc3545",
      };
      return colors[difficulty] || "#6c757d";
    },

    getDietaryColor(tag) {
      const colors = {
        vegetarian: "#28a745",
        vegan: "#20c997",
        "gluten-free": "#ffc107",
        "dairy-free": "#17a2b8",
      };
      return colors[tag] || "#6c757d";
    },

    formatDietaryTag(tag) {
      return tag
        .replace("-", " ")
        .replace(/\b\w/g, (l) => l.toUpperCase());
    },

    formatTimingWindow(window) {
      const windows = {
        "3-4hrs_pre_match": "3-4 hours before match",
        "1-2hrs_pre_training": "1-2 hours before training",
        "60mins_post_match": "Within 60 mins after match",
        "60mins_post_training": "Within 60 mins after training",
        morning: "Morning",
        evening: "Evening",
      };
      return windows[window] || window;
    },
  },
};
</script>

<style lang="scss" scoped>
.recipe-card {
  background: white;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  }

  &.expanded {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  }

  &.favorite {
    border-left: 3px solid #ffc107;
  }
}

.recipe-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.recipe-main-info {
  flex: 1;
}

.recipe-badges {
  display: flex;
  gap: 6px;
  margin-bottom: 8px;
}

.category-badge,
.difficulty-badge {
  font-size: 0.7rem;
  font-weight: 600;
  color: white;
  padding: 2px 8px;
  border-radius: 10px;
  text-transform: uppercase;
}

.recipe-name {
  font-size: 1rem;
  font-weight: 600;
  color: #333;
  margin: 0 0 4px 0;
}

.recipe-description {
  font-size: 0.85rem;
  color: #666;
  margin: 0;
  line-height: 1.4;
}

.recipe-actions {
  margin-left: 12px;
}

.favorite-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: #f8f9fa;
  cursor: pointer;
  font-size: 1.2rem;
  color: #ccc;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;

  &:hover {
    background: #fff3cd;
    color: #ffc107;
  }

  &.active {
    background: #fff3cd;
    color: #ffc107;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.recipe-meta {
  margin-bottom: 8px;
}

.meta-row {
  display: flex;
  gap: 16px;
  margin-bottom: 10px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.meta-icon {
  width: 22px;
  height: 22px;
  border-radius: 5px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 700;
  color: white;

  &.meal-type-icon {
    background: linear-gradient(135deg, #6f42c1, #9561e2);
  }

  &.time-icon {
    background: linear-gradient(135deg, #6c757d, #868e96);
  }

  &.cal-icon {
    background: linear-gradient(135deg, #fd7e14, #f08c00);
  }
}

.meta-value {
  font-size: 0.8rem;
  color: #555;
}

.macros-row {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
}

.macro-badge {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 15px;
  font-size: 0.75rem;

  &.carbs {
    background: rgba(255, 193, 7, 0.15);
    color: #cc9a06;
  }

  &.protein {
    background: rgba(220, 53, 69, 0.15);
    color: #a71d2a;
  }

  &.fat {
    background: rgba(40, 167, 69, 0.15);
    color: #1e7e34;
  }
}

.macro-letter {
  font-weight: 700;
}

.macro-value {
  font-weight: 500;
}

.dietary-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.dietary-tag {
  font-size: 0.65rem;
  font-weight: 600;
  color: white;
  padding: 2px 8px;
  border-radius: 8px;
}

.recipe-details {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #eee;
}

.detail-section {
  margin-bottom: 16px;

  h5 {
    font-size: 0.85rem;
    font-weight: 600;
    color: #333;
    margin: 0 0 6px 0;
  }

  p {
    font-size: 0.85rem;
    color: #666;
    margin: 0;
    line-height: 1.5;
  }
}

.timing-info {
  display: inline-block;
  background: #e3f2fd;
  color: #1565c0;
  padding: 4px 10px;
  border-radius: 6px;
  font-weight: 500;
}

.quick-add-btn {
  width: 100%;
  padding: 12px;
  border: none;
  border-radius: 8px;
  background: linear-gradient(135deg, #28a745, #20c997);
  color: white;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
  }
}

.expand-indicator {
  text-align: center;
  color: #aaa;
  font-size: 0.8rem;
  margin-top: 8px;
}
</style>
